import { countBy } from 'lib/array/arrayUtils'

interface FormParsingOptions {
  includeDisabledFields?: boolean
}

/**
 * Parses a form and returns an array of key/value pairs
 * from the elements with values in the form
 * The key being the 'name' of the element and value being it's value
 * @param form HTMLFormData The form to parse
 */
export function inputElementsToEntries(
  elements: HTMLFormControlsCollection | Array<HTMLElement>,
  options: FormParsingOptions = {},
): Array<[string, string | Array<string>, HTMLElement]> {
  const {
    includeDisabledFields = false,
  } = options

  const formValues: Array<[string, string | Array<string>, HTMLElement]> = []
  const invalidChildInputs = new Set<HTMLElement>()

  for (let i = 0; i < elements.length; i++) {
    const elm = elements[i] as HTMLInputElement
    const shouldIgnore = !includeDisabledFields && elm.disabled
    const isInvalid = !elm.name || shouldIgnore || elm.type === 'submit' || elm.type === 'button' ||
      elm.type === 'file' || elm.type === 'fieldset' || invalidChildInputs.has(elm)

    if (isInvalid) {
      if (elm.type === 'fieldset' && shouldIgnore) {
        Array.from((elm as unknown as HTMLFieldSetElement).elements).forEach((elm) => { invalidChildInputs.add(elm as HTMLElement) })
      }
      continue
    }

    if (elm.type === 'select-one') {
      const select = elm as unknown as HTMLSelectElement
      for (let o = 0; o < select.options.length; o++) {
        const option = select.options[o]
        if (!option.disabled && option.selected) {
          formValues.push([String(elm.name), String(option.value), elm])
        }
      }
      continue
    }

    if (elm.type === 'select-multiple') {
      const select = elm as unknown as HTMLSelectElement
      const selectedValues: Array<string> = []

      for (let o = 0; o < select.options.length; o++) {
        const option = select.options[o]
        const shouldIgnoreOption = !includeDisabledFields && option.disabled
        if (!shouldIgnoreOption && option.selected) {
          selectedValues.push(String(option.value))
        }
      }
      formValues.push([String(elm.name), selectedValues, elm])
      continue
    }

    if (elm.type === 'checkbox' && elm.name.endsWith('::boolean')) {
      // Native checkboxs that are not checked should not be included in the form data response
      // However, if we're explicitly said we want a boolean - we're going to treat
      // not checked as a 'false' instead
      const value = elm.checked ? elm.value : ''
      formValues.push([String(elm.name), String(value), elm])
      continue
    }

    if (elm.type === 'checkbox' || elm.type === 'radio') {
      // Native checkboxs/radios that are not checked should not be included in the form data response
      if (elm.checked) {
        formValues.push([String(elm.name), String(elm.value), elm])
      }
      continue
    }

    if (elm.type === 'textarea') {
      // normalize the line endings between each new line
      const value = elm.value.replace(/\r\n/g, '\n').replace(/\n/g, '\r\n')
      formValues.push([String(elm.name), String(value), elm])
      continue
    } else {
      formValues.push([String(elm.name), String(elm.value), elm])
      continue
    }
  }

  return formValues
}

function parseValue(value: any, type: string) {
  switch (type) {
    case 'number':
      return parseInt(value, 10)
    case 'float':
      return parseFloat(value)
    case 'boolean':
      return !!value
    case 'string':
      return value
  }
  return value
}

function elementEntriesToObject<T extends Record<string, any>>(entries: Array<[string, any, HTMLElement]>): T {
  const obj = {} as T
  const allNames = countBy(entries.map(entry => entry[0].split('::')[0]), val => val)

  for (const entry of entries) {
    const [key, value] = entry

    // supports the form foo.bar.xyz::type
    const [realKey, type = 'string'] = key.split('::')
    const attributes = realKey.split('.')
    attributes.reduce<Record<string, any>>((formData, attribute, index) => {
      const isBottom = index === attributes.length - 1

      if (attribute.endsWith(']')) {
        // it's an array prop!
        const [attributeName, indexStr] = attribute.split('[')
        const arrayIndex = indexStr.slice(0, indexStr.length - 1)
        formData[attributeName] = formData[attributeName] || []
        if (isBottom) {
          // if it's the bottom attribute, then we are assigning the value
          const parsedValue = parseValue(value, type)

          const realKeyValue = allNames.get(realKey)
          if (realKeyValue !== undefined && realKeyValue > 1) {
            // multiple inputs with same name, must want an array
            if (!Array.isArray(formData[attributeName][arrayIndex])) {
              formData[attributeName][arrayIndex] = []
            }
            formData[attributeName][arrayIndex].push(parsedValue)
          } else {
            // only one input with this name, single value
            formData[attributeName][arrayIndex] = parsedValue
          }
        } else {
          formData[attributeName][arrayIndex] = formData[attributeName][arrayIndex] || {}
          return formData[attributeName][arrayIndex]
        }
      } else if (isBottom) {
        // if it's the bottom attribute, then we are assigning the value
        const parsedValue = parseValue(value, type)
        const realKeyValue = allNames.get(realKey)
        if (realKeyValue !== undefined && realKeyValue > 1) {
          // multiple inputs with same name, must want an array
          if (!Array.isArray(formData[attribute])) {
            formData[attribute] = []
          }
          formData[attribute].push(parsedValue)
        } else {
          // only one input with this name, single value
          formData[attribute] = parsedValue
        }
        return formData[attribute]
      } else {
        formData[attribute] = formData[attribute] || {}
        return formData[attribute]
      }
    }, obj)
  }

  return obj
}

/**
* Transforms a form into an object describing the data from the fields in it
* Parses names that have [] as arrays with index specified e.g. foo[1].bar
* Also recognises dot notation as sub-objects (e.g. foo.bar.xyz)
* This also supports forcing parsing of types by adding a ":<type>" to the
* value. e.g. foo.bar.xyz:number
* @param form HTMLFormElement data object
*/
export function formToObject<T extends Record<string, any>>(form: HTMLFormElement | HTMLFieldSetElement, options?: FormParsingOptions): T {
  const elementEntries = inputElementsToEntries(form.elements, options)
  return elementEntriesToObject<T>(elementEntries)
}

export function elementsToObject<T extends Record<string, any>>(elements: Array<HTMLElement>, options?: FormParsingOptions) {
  const elementEntries = inputElementsToEntries(elements, options)
  return elementEntriesToObject<T>(elementEntries)
}
